import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The Today tile (canvas `3a`–`3e`, the shared primitive). M5 builds it and uses it once, for
 * the pre-bite panel; M6a composes the five bite tiles out of it.
 *
 * Two rules from the canvas that are easy to lose: there is **no chevron** — the whole tile is
 * the target — and the tile carries no shadow or hover lift. It is a surface, not a button
 * pretending to be a card.
 */

/** Icon-square tints. The canvas uses ember for watch/listen, sky for review/pattern, neutral
 *  for reading — one axis per colour, never decoration. */
export type TileTint = 'ember' | 'sky' | 'neutral';

const TINTS: Record<TileTint, { background: string; color: string }> = {
  ember: {
    background: 'color-mix(in srgb, var(--nn-accent) 14%, transparent)',
    color: 'var(--nn-accent)',
  },
  sky: {
    background: 'color-mix(in srgb, var(--nn-link) 13%, transparent)',
    color: 'var(--nn-link)',
  },
  neutral: {
    background: 'color-mix(in srgb, var(--nn-text) 6%, transparent)',
    color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
  },
};

/** 30px icon square + 11px gap — what a tile's sub-content lines up under (design README §5). */
export const TILE_INDENT = 41;

/**
 * M6a's three additional shapes (the canvas draws each of them):
 *   `compact`   — the half-row Listen and One-pattern tiles (`2b`): a 24 px square and 12/10
 *                 type, two to a row. Smaller because they are the shortest things in the day,
 *                 not because they matter less.
 *   `receipt`   — what the review tile flips to when the run is done (`3e`). "The flip is the
 *                 only celebration — a receipt, not confetti."
 *   `completed` — the evening state's dimmed rows (`2c`), each with a replay.
 */
export type TileVariant = 'default' | 'compact' | 'receipt' | 'completed';

export interface TileProps {
  icon?: ReactNode;
  tint?: TileTint;
  title: ReactNode;
  meta?: ReactNode;
  /** Sub-content under the header row, indented to the text column. */
  children?: ReactNode;
  /** The plain-language "why this?" strip. Never algorithmic (design README §6). */
  why?: ReactNode;
  to?: string;
  style?: CSSProperties;
  variant?: TileVariant;
  /** 0..1 — the 3 px bar pinned to the tile's bottom edge (the watch tile's watched fraction). */
  progress?: number;
  /** the evening state's trailing "replay" affordance */
  action?: ReactNode;
}

export function Tile({
  icon,
  tint = 'neutral',
  title,
  meta,
  children,
  why,
  to,
  style,
  variant = 'default',
  progress,
  action,
}: TileProps) {
  const compact = variant === 'compact' || variant === 'completed';
  const square = compact ? 24 : 30;
  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 11 }}>
        {icon && (
          <span
            aria-hidden
            style={{
              width: square,
              height: square,
              borderRadius: compact ? 7 : 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ...TINTS[tint],
            }}
          >
            {icon}
          </span>
        )}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: compact ? 12 : 13.5,
              color: 'var(--nn-text-strong)',
              ...(compact
                ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                : {}),
            }}
          >
            {title}
          </span>
          {meta && (
            <span style={{ fontSize: compact ? 10 : 11, color: 'var(--nn-muted)' }}>{meta}</span>
          )}
        </span>
        {action && (
          <>
            <span style={{ flex: 1 }} />
            {action}
          </>
        )}
      </div>
      {children && (
        <div style={{ marginTop: 7, paddingLeft: icon ? (compact ? square + 8 : TILE_INDENT) : 0 }}>
          {children}
        </div>
      )}
      {why && (
        <div
          style={{
            marginTop: 7,
            background: 'color-mix(in srgb, var(--nn-text) 5%, transparent)',
            borderRadius: 7,
            padding: '5px 9px',
            fontSize: 10.5,
            lineHeight: 1.45,
            color: 'var(--nn-muted)',
          }}
        >
          {why}
        </div>
      )}
    </>
  );

  const shell: CSSProperties = {
    position: 'relative',
    background: 'var(--nn-panel)',
    border: '1px solid var(--nn-hairline)',
    borderRadius: variant === 'completed' ? 9 : 10,
    padding: variant === 'completed' ? '8px 12px' : compact ? '9px 11px' : '11px 13px 12px',
    display: 'block',
    overflow: progress !== undefined ? 'hidden' : undefined,
    ...style,
  };

  /**
   * The 3 px bar sits UNDER the content, pinned to the tile's bottom edge, rather than being a
   * row of its own — the canvas draws it bleeding into the tile's corners, and a bar with
   * padding around it would read as a component instead of as a fill level.
   */
  const bar = progress !== undefined && progress > 0 && (
    <>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 3,
          width: '100%',
          background: 'color-mix(in srgb, var(--nn-text) 5%, transparent)',
        }}
      />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          height: 3,
          width: `${Math.min(100, Math.max(0, progress * 100))}%`,
          background: 'var(--nn-accent)',
        }}
      />
    </>
  );

  // `completed` dims the tile's CONTENT, not its shell: the replay beside it has to stay
  // legible, and dimming the whole row would dim the one thing on it that is still a control.
  const content =
    variant === 'completed' ? (
      <span style={{ display: 'block', opacity: 0.45 }}>{body}</span>
    ) : (
      body
    );

  return to ? (
    <Link to={to} style={{ ...shell, textDecoration: 'none', color: 'inherit' }}>
      {content}
      {bar}
    </Link>
  ) : (
    <div style={shell}>
      {content}
      {bar}
    </div>
  );
}
